import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { disableFeatureOnError } from '@entities/account';
import {
  useSuperlikeSessionStore,
  withoutAnsweredSenders,
} from '@entities/superlike/model';

import { useGetSuperlikesIncoming } from '@services/hooks/SuperlikesService';
import { useSseEngineContext } from '@services/SSE';
import { type IncomingSuperlikeCard } from '@services/SuperlikesService';

import { SUPERLIKE_SWR_DEDUPE_MS } from './useSuperlikeQuota';

export const SUPERLIKES_INCOMING_LIMIT = 20;
/** A burst of `likes_received` events costs one request. */
export const SUPERLIKES_INCOMING_SSE_DEBOUNCE_MS = 1500;

export type UseIncomingSuperlikesParams = {
  isEnabled: boolean;
};

/**
 * The superlikes the user received and has not answered, shared by the feed
 * and the Likes tab (one SWR key, deduped for a minute). A page can come back
 * empty with a cursor (the server reads at most 200 rows per call); then the
 * next page is fetched once. A `likes_received` event revalidates, debounced.
 * `cards` is undefined until the first answer; `isSettled` is true once the
 * list answered or failed (always, without the feature).
 */
export const useIncomingSuperlikes = ({
  isEnabled,
}: UseIncomingSuperlikesParams) => {
  const { addSseHandler, removeSseHandler } = useSseEngineContext();
  const answeredSenderIds = useSuperlikeSessionStore(
    (state) => state.answeredSenderIds,
  );
  const swrOptions = useMemo(
    () => ({
      dedupingInterval: SUPERLIKE_SWR_DEDUPE_MS,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      onError: (error: unknown) => {
        disableFeatureOnError(error, 'superlike');
      },
    }),
    [],
  );

  const firstPage = useGetSuperlikesIncoming({
    isEnabled,
    queryParams: { limit: SUPERLIKES_INCOMING_LIMIT },
    swrOptions,
  });
  const nextCursor =
    firstPage.data && firstPage.data.results.length === 0
      ? firstPage.data.next_cursor
      : null;
  const nextPage = useGetSuperlikesIncoming({
    isEnabled: isEnabled && nextCursor !== null,
    queryParams: {
      limit: SUPERLIKES_INCOMING_LIMIT,
      cursor: nextCursor ?? undefined,
    },
    swrOptions,
  });

  // The SSE context hands out new functions on each of its renders, and a
  // re-registration would drop a pending debounce: the effect reads them,
  // and the revalidation, from refs.
  const sseRef = useRef({ addSseHandler, removeSseHandler });
  const revalidateRef = useRef(firstPage.mutate);

  useLayoutEffect(() => {
    sseRef.current = { addSseHandler, removeSseHandler };
    revalidateRef.current = firstPage.mutate;
  });

  useEffect(() => {
    if (!isEnabled) return;

    const { addSseHandler: add, removeSseHandler: remove } = sseRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onLikesReceived = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        void revalidateRef.current();
      }, SUPERLIKES_INCOMING_SSE_DEBOUNCE_MS);
    };

    add('likes_received', onLikesReceived);

    return () => {
      clearTimeout(timer);
      remove('likes_received', onLikesReceived);
    };
  }, [isEnabled]);

  const isFirstPageSettled =
    firstPage.data !== undefined || firstPage.error !== undefined;
  const isNextPageSettled =
    nextCursor === null ||
    nextPage.data !== undefined ||
    nextPage.error !== undefined;

  // Undefined until every page that counts has answered: an empty first
  // page with a cursor waits for the next one, so nobody counts the list
  // before its cards are in. A failed next page leaves the empty first.
  const cards = useMemo((): IncomingSuperlikeCard[] | undefined => {
    if (!isEnabled || !firstPage.data) return undefined;

    if (firstPage.data.results.length > 0 || nextCursor === null) {
      return withoutAnsweredSenders(firstPage.data.results, answeredSenderIds);
    }

    if (nextPage.data) {
      return withoutAnsweredSenders(nextPage.data.results, answeredSenderIds);
    }

    return nextPage.error === undefined ? undefined : [];
  }, [
    answeredSenderIds,
    firstPage.data,
    isEnabled,
    nextCursor,
    nextPage.data,
    nextPage.error,
  ]);

  return {
    cards,
    isSettled: !isEnabled || (isFirstPageSettled && isNextPageSettled),
  };
};
